import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import { wsChannel } from '@xdocs/schemas';
import type { Document, ErrorAlert } from '@xdocs/schemas';
import { useWs } from '../../ws/WsProvider.js';

export interface UseDocumentResult {
  document: Document | undefined;
  isLoading: boolean;
  isError: boolean;
  error: ErrorAlert | null;
  refetch: () => void;
}

/** Single-document read (`document.get`), kept live via the
 * `document:{documentId}` push channel — "signal, not payload" (the push
 * carries only `{documentId, versionId, origin}`; this hook just re-fetches
 * on receipt, per `documentVersionPush`'s own doc comment). */
export function useDocument(documentId: string): UseDocumentResult {
  const { wsClient, connectionState } = useWs();
  const queryClient = useQueryClient();
  const enabled = connectionState === 'connected';

  const query = useQuery<Document, ErrorAlert>({
    queryKey: ['document', documentId],
    queryFn: () => requestTyped(wsClient, 'document.get', { documentId }),
    enabled,
  });

  useEffect(() => {
    const channel = wsChannel.document(documentId);
    return wsClient.subscribe(channel, () => {
      void queryClient.invalidateQueries({ queryKey: ['document', documentId] });
    });
  }, [wsClient, queryClient, documentId]);

  return {
    document: query.data,
    // `isPending`, NOT `isLoading` (`isPending && isFetching`): while the
    // socket is still connecting the query is DISABLED, so `isFetching` is
    // false and `isLoading` reports false with no data — which made
    // `DocumentDetailPage` fall through to its "couldn't find this
    // document" branch during a perfectly normal boot/reconnect window
    // (observed on hard reload of `/documents/:id` right after the E20
    // auth-resume landed). Pending-without-data IS a loading state here.
    isLoading: query.isPending,
    isError: query.isError,
    error: query.isError ? query.error : null,
    refetch: () => void query.refetch(),
  };
}
